import { ParallelOptions, piterate } from './piterate';

/**
 * Allows async iterating of an iterable; returns results to a second callback.
 * Result order IS guaranteed to match input order.
 * @param iterable
 * @param fnMapper
 * @param fnResult
 * @param options
 */
export function pqueue<T, U>(
	iterable: Iterable<T>,
	fnMapper: (item: T, index: number) => Promise<U> | U,
	fnResult: (result: U, index: number) => void,
	options?: ParallelOptions
): Promise<void> {
	let nextNeededResultIdx = 0;
	const queuedResults = new Map<number, U>();

	return piterate(
		iterable,
		fnMapper,
		(result, index) => {
			if (nextNeededResultIdx === index) {
				// This result is the next needed, so just emit right away
				fnResult(result, index);
				nextNeededResultIdx++;

				// There may be other results queued behind this one, so emit as much as possible
				while (queuedResults.size > 0 && queuedResults.has(nextNeededResultIdx)) {
					fnResult(queuedResults.get(nextNeededResultIdx)!, nextNeededResultIdx);
					queuedResults.delete(nextNeededResultIdx);
					++nextNeededResultIdx;
				}
			} else {
				// We cannot send this result yet...
				queuedResults.set(index, result);
			}
		},
		options
	);
}
